//REV-21368 — the shared vocabulary of the ABCP document flow (ADR-0009).
//
// PROMOTED out of `components/documents-hub/documents-hub.types.ts`, where REV-21367 first
// declared it. Two sibling pure views now speak it — the hub (REV-21367) and the single-document
// page (REV-21368) — and a component reaching into another component's folder for its types is the
// coupling this move removes. `documents-hub.types.ts` re-exports everything below, so REV-21367's
// open PR keeps compiling unchanged.
//
// Nothing here is computed by a view. The upstream owners (ADR-0009):
//   `filing` / `notRelevant` / `source` / `validity` → closing_documents (REV-21376, §1)
//   assignment / account / confidence                → read-only in the FE, written by REV-21602 (§9)
//   `kind` / `confidence` / proposal state           → produced by REV-21358 (§8)

/**
 * Where a document is filed — the POSITIVE discriminator from ADR-0009 §9a.
 * `projectFile` = relevant for the closing but attached to no account (Projekt-Akte); it is the
 * OPPOSITE of `notRelevant`, not a variant of it. Both lack an account, which is exactly why
 * "has no account" must not be the representation.
 */
export type DocumentFiling = 'unfiled' | 'account' | 'projectFile';

/** Where the document came from. `email` is future data: rendered, never produced (ADR-0009 §8/D6). */
export type DocumentSource = 'upload' | 'sharepoint' | 'email';

/**
 * The assignment state, DERIVED UPSTREAM and never in a view (ADR-0009 §9):
 * account set → `assigned`; else a live proposal → `proposed`; else `open`.
 */
export type AssignmentState = 'open' | 'proposed' | 'assigned';

/** Who put the account there. Drives the confirmed chip's icon only, never its meaning. */
export type AssignmentOrigin = 'manual' | 'agent';

/** `longTerm` = Dauerakte: carries into next year's closing (ADR-0009 §5). */
export type DocumentValidity = 'closing' | 'longTerm';

/**
 * An account as a document's CHIP shows it. `label` arrives PRE-COMPOSED ("1000 Kasse") — the view
 * never concatenates a number with a name, so the account plan's display rule stays in one place.
 */
export interface DocumentAccountRef {
    id: string;
    label: string;
}

/**
 * An account as the PICKER lists it. Both pickers render number, name and class as separate
 * columns, so unlike the chip they cannot take a pre-composed label.
 *
 * `group` is never rendered — both pickers match number OR name OR account group, so a document
 * can be found by the group it belongs to without that group taking up a column.
 */
export interface AccountOption {
    id: string;
    number: string;
    name: string;
    /** The account class, shown muted at the right edge of the row. */
    klasse: string;
    group?: string;
}

/**
 * Where a document is being filed. `projectFile` is a first-class target in both pickers, not the
 * absence of an account (ADR-0009 §9a).
 */
export type AssignmentTarget = { kind: 'account'; accountId: string } | { kind: 'projectFile' };

/**
 * Payload of `markNotRelevant` — ONE output for both directions, carrying the new value. Two
 * separate outputs would let a caller wire only half of a reversible flag.
 */
export interface MarkNotRelevantEvent {
    documentId: string;
    notRelevant: boolean;
}

/**
 * Payload of the HUB's filing gesture. Consumed by REV-21602's endpoint, unwired until then.
 *
 * `validity` rides along because the hub's picker asks for it as a second step — the hub has
 * nowhere else to ask. The single-document page (REV-21368) does: a standing `Gültigkeit` panel
 * sits in its sidebar, so its picker is one step and emits `DocumentAssignmentChangeEvent`
 * (target only). Two payloads, because the two surfaces genuinely ask different questions.
 *
 * It lives here rather than in the hub's folder so the whole filing vocabulary has ONE home and
 * REV-21602 can consume both shapes from one import.
 */
export interface AccountAssignEvent {
    documentId: string;
    target: AssignmentTarget;
    validity: DocumentValidity;
}

/**
 * REV-21964 · C1–C4 — where a document's upstream file stands.
 *
 * Part of the SHARED vocabulary because both surfaces show it: the hub as a row marker, the
 * single-document page as a notice. Declaring it twice is how the two would come to disagree about
 * what `movedOut` means.
 *
 * ⚠️ It is a LABEL. Nothing in either view may hide a row, detach an account or remove a document
 * because of it — a deleted upstream file leaves the document, its account and its reconciliation
 * exactly where they were (C1), and ADR-0009 §4 has no delete path at all. `notRelevant` stays the
 * only retreat, and it stays the trustee's.
 */
export type DocumentUpstreamState = 'present' | 'deleted' | 'movedOut' | 'replaced';

/**
 * REV-22010 — whether the AI has READ the document's file, as `aumico-be` derives it at read time
 * from the file's extraction bookkeeping (`document-reading-state.ts`). ABSENT means "read, nothing to
 * say" — the resting value, omitted on the wire like `upstreamState: 'present'`.
 *
 * Shared vocabulary: the hub's account column reads it (`presenters/document-ai-state.ts`), the
 * single-document page shows it as a line in its AI panel. `failed` means ABANDONED (the attempt limit was
 * reached); a failure with retries left arrives as `reading`.
 *
 * ⚠️ A LABEL, like the upstream state: it hides nothing and changes nothing else on the row.
 */
export type DocumentReadingState = 'pending' | 'reading' | 'deferredDailyCap' | 'failed' | 'notSupported';

/** REV-22302 — which strand carried the proposed account: the document's content, its balance, or both. */
export type ProposalSource = 'CONTENT' | 'BALANCE' | 'BOTH';
/** Absent = normal certainty, no marker. */
export type ProposalCertainty = 'HIGH' | 'MEDIUM';
export type BalanceCheck = 'MATCHES' | 'DIFFERS' | 'NOT_CHECKED';
/** The pipeline step a document is in right now; absent when nothing runs. */
export type DocumentProcessingStep = 'SCAN' | 'TEXT' | 'KIND' | 'READING';

// ─── REV-21964 · R1/R3/U1 — the vocabulary of a SYNC RUN ────────────────────────────────────────
//
// Here and not in `models/closing-document.model.ts` for the reason that file states about every
// other union it uses: the backend already answers in this spelling, the hub renders it, and a
// second declaration is a second place for the two to drift. The model re-exports these, so every
// existing import path keeps working.

/**
 * Why one file did not become a document.
 *
 * ⚠️ This list had DRIFTED before REV-21964 realigned it: the backend has had `tooLarge` since
 * 21.08.2026 (REV-21376 F19) and the frontend never gained it, so an oversized file arrived as a
 * reason the UI could not name. That is the quiet half of a drift — nothing throws, the user simply
 * reads nothing useful. Every member below exists on the backend, and every backend member is here.
 */
export type SharepointSkipReason =
    /** Not PDF, XLSX or DOCX. Replaced `notPdf`, which stopped being true when the set widened. */
    | 'unsupportedType'
    | 'alreadyImported'
    | 'downloadFailed'
    | 'rejectedByIngest'
    | 'listingCapped'
    /** Over the 32 MB limit. On the backend since REV-21376 F19; missing here until now. */
    | 'tooLarge'
    /**
     * ID2 — the same BYTES already exist in this closing under a different upstream file; the other
     * name is kept as an alias on the document we already hold.
     *
     * Deliberately distinct from `alreadyImported`: that means "this very file, unchanged", this
     * means "you have these bytes twice in SharePoint" — a fact about the trustee's own folder.
     */
    | 'duplicateContent';

/** How far a run got. */
export type SyncRunStatus = 'pending' | 'running' | 'completed' | 'failed';

/**
 * Why a start request was refused.
 *
 * ⚠️ All three are ANSWERS, not errors. Rendering one as a failure invites the user to press the
 * button again, which is the wrong advice in all three cases: sealed stays sealed (R6), a run
 * already in flight will finish on its own, and "no folder connected" is a trip to the settings
 * modal.
 */
export type SyncStartRefusal = 'closingSealed' | 'alreadyRunning' | 'noBoundFolders';

// ─── REV-21964 · T1–T4 — what the closing ACCEPTS ───────────────────────────────────────────────

/**
 * The file types a closing takes in, as the browser's `accept` attribute wants them.
 *
 * ⚠️ CONVENIENCE, NOT A BOUNDARY. This list only shapes the file picker and the modal's local
 * pre-check; the binding decision is the server's, and it reads MAGIC NUMBERS rather than a name or
 * a declared type (`ABCP_INGEST_ALLOWLIST` in the backend's domain layer). A file renamed to `.pdf`
 * passes this and is still refused there. Nothing here may be mistaken for a control.
 *
 * ⚠️ MIME **and** extension for each type, deliberately. Some browsers hand over an empty `type`
 * for a file dragged from certain sources, and without the extensions such a file would be rejected
 * locally as "wrong kind" before the server ever saw it — which is exactly how Excel and Word
 * stayed unuploadable after the server had long accepted them (Code-Review Runde 1, B3).
 *
 * The legacy Office formats `.doc`/`.xls` are absent on purpose: the backend cannot tell them apart
 * by content (both report as `cfb`), so accepting them here would promise something it refuses.
 */
export const ABCP_INGEST_ACCEPT = [
    'application/pdf',
    '.pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.xlsx',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.docx',
].join(',');
